// src/app/_service/startup.service.ts
import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

import { DataService } from './data.service';
import { UserSettingsService } from './user-settings.service';
import { AbandonedSessionRecoveryService } from './abandoned-session-recovery.service';
import { SpaceContextService } from './space-context.service';
import { SpaceDirectoryService } from './space-directory.service';

@Injectable({ providedIn: 'root' })
export class StartupService {
  constructor(
    private dataService: DataService,
    private userSettingsService: UserSettingsService,
    private router: Router,   // ✅ inject router properly
    private abandonedSessionRecovery: AbandonedSessionRecoveryService,
    private spaceContext: SpaceContextService,
    private spaceDirectory: SpaceDirectoryService,
  ) {}

  async initialize(): Promise<void> {
    try {
      console.log('[Startup] Initializing Notes-App data...');

      // 1️⃣ Load data and sync (user settings already initialized via APP_INITIALIZER)
      await this.dataService.initialize();

      // Never blocks startup (offline-first) — see SpaceContextService.initialize().
      void this.spaceContext.initialize();
      // tasks/space_ux.md — the Space name directory used by the Home filter/column, View, and
      // Manage Spaces. Also never blocks: it just refreshes the cache SpaceDirectoryService
      // already loaded synchronously from localStorage in its constructor.
      this.spaceDirectory.refresh();

      console.log('[Startup] Initialization complete.');

      // ✅ Optional: Safari-specific repaint fix (moved *after* initialization)
      this.setupSafariRepaintFix();

      // Autosave Stage 3 — look for opening snapshots left behind by edit
      // sessions that never closed normally. Never blocks startup on this;
      // any failure is caught and logged inside the service itself too.
      void this.abandonedSessionRecovery.checkForAbandonedSessions();

    } catch (err) {
      console.error('[Startup] Initialization failed', err);
    }
  }

  /** Safari sometimes caches transforms after route navigation. */
  private setupSafariRepaintFix(): void {
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      requestAnimationFrame(() => {
        document.body.style.display = 'none';
        // force reflow
        document.body.offsetHeight;
        document.body.style.display = '';
      });
    });
  }
}
